import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const btn = "h-11 w-full rounded-md bg-cyan-500 text-slate-950 font-semibold flex items-center justify-center gap-2 hover:bg-cyan-400 transition-colors disabled:opacity-50";

const EmailStep = ({ email, setEmail, onSend, busy }) => (
  <form onSubmit={onSend} className="space-y-3">
    <label htmlFor="otp-email" className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Email address</label>
    <div className="relative">
      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input id="otp-email" data-testid="otp-email-input" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com" className="h-11 w-full rounded-md bg-[#090D16] border border-slate-700 pl-9 pr-3 text-sm focus:outline-none focus:border-cyan-500 transition-colors" />
    </div>
    <button data-testid="otp-send-btn" type="submit" disabled={busy || !email} className={`${btn} group`}>
      {busy ? <Loader2 size={16} className="animate-spin" /> : null}Email me a sign-in code<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
    </button>
  </form>
);

const CodeStep = ({ email, code, setCode, onVerify, onResend, onBack, busy, wait }) => (
  <div className="space-y-4">
    <p className="text-sm text-slate-400" data-testid="otp-sent-to">Enter the 6-digit code sent to <span className="text-slate-100">{email}</span></p>
    <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={onVerify} autoFocus data-testid="otp-code-input" inputMode="numeric" pattern="^[0-9]*$">
      <InputOTPGroup className="gap-2">
        {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 rounded-md border border-slate-700 bg-[#090D16] font-mono text-lg text-cyan-300 first:rounded-md last:rounded-md" />)}
      </InputOTPGroup>
    </InputOTP>
    <button data-testid="otp-verify-btn" onClick={() => onVerify(code)} disabled={busy || code.length !== 6} className={btn}>
      {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}Verify & sign in
    </button>
    <div className="flex items-center justify-between text-xs">
      <button data-testid="otp-change-email-btn" onClick={onBack} className="text-slate-400 hover:text-cyan-300 transition-colors">Change email</button>
      <button data-testid="otp-resend-btn" onClick={onResend} disabled={wait > 0 || busy} className="text-cyan-400 hover:text-cyan-300 disabled:text-slate-600 transition-colors">
        {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
      </button>
    </div>
  </div>
);

export const OtpLogin = () => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [wait, setWait] = useState(0);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); } };
  const send = (e) => { e?.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/otp/request", { email: email.trim() });
    setStep("code"); setCode(""); setWait(data.resend_after); toast.success(`Code sent to ${email.trim()}`);
  }); };
  const verify = (value) => run(async () => {
    try {
      await api.post("/auth/otp/verify", { email: email.trim(), code: value });
      const { data } = await api.get("/auth/me");
      setUser(data); navigate("/dashboard");
    } catch (e) { setCode(""); throw e; }
  });
  return (
    <div className="rounded-lg border border-slate-800 bg-[#0B0F19]/90 backdrop-blur-md p-6 space-y-4 shadow-[0_0_60px_-25px_rgba(6,182,212,0.4)]" data-testid="otp-login-card">
      <div>
        <h2 className="font-heading text-base md:text-lg font-semibold">Sign in</h2>
        <p className="text-xs text-slate-500 mt-1">Private workspace for invited team members. Sign in with a one-time email code.</p>
      </div>
      {step === "email"
        ? <EmailStep email={email} setEmail={setEmail} onSend={send} busy={busy} />
        : <CodeStep email={email.trim()} code={code} setCode={setCode} onVerify={verify} onResend={send} onBack={() => { setStep("email"); setErr(""); }} busy={busy} wait={wait} />}
      {err && <p className="text-xs text-rose-400" data-testid="otp-error" role="alert">{err}</p>}
    </div>
  );
};
